import "./CaseEditForm.css";

import { faMicroscope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import { capitalize } from "lodash";
import { DateTime } from "luxon";
import { useState, useContext } from "react";

import { Case, CaseAdmin, CliaCertificate } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { selectExternalLabsForLab } from "shared/selectors/externalLabSelectors";
import { caseSchema, caseWarningSchema } from "shared/utils/caseSchema";
import { getBillingProperties } from "shared/utils/caseUtils";
import {
  displayPriority,
  isBillingTypeUnsupported,
} from "shared/utils/caseUtils";
import { getZoneString } from "shared/utils/dates";
import { isNoBillingConstrainedCaseType } from "shared/utils/rulesEngineUtils";
import {
  canValidate,
  getErrors,
  getValidationWarnings,
  getValueFromObject,
} from "shared/utils/validationUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

import CaseEditPopUp from "./CaseEditPopUp";
import { getSelfPayProperties, initialValuesForCase } from "./caseFormUtils";
import FormErrors from "./FormErrors";
import FormikContext from "./FormikContext";
import { UnsupportedBillingTypeContext } from "./UnsupportedBillingTypeContext";
import {
  AdminFieldAssignment,
  AdminFieldGrossedByCert,
  AdminFieldLab,
  AdminFieldSignature,
} from "../edit/fields";
import {
  CliaCertificateOption,
  Option,
  UserOption,
} from "../edit/ResourceOptions";

type CaseEditFormProps = {
  accessionCompleted?: string | null;
  adminCase: CaseAdminDetail;
  onSubmit: any;
  setValues: Function;
  setIsDeleting: Function;
  isEdited: boolean;
  setEdited: Function;
  canUpdateCase: boolean;
};

const enabledStatuses = [
  Case.status.ACTIVE,
  Case.status.WORKING,
  Case.status.FINALIZED,
];

const enabledCasetypes = [
  Case.type.CLINICAL,
  Case.type.CONSULT,
  Case.type.HISTORICAL,
  Case.type.TC_PC,
];

const currentBillingTypes = [
  CaseAdmin.billing_type.GLOBAL,
  CaseAdmin.billing_type.CLIENT,
  CaseAdmin.billing_type.NONE,
];

const CaseEditForm = ({
  accessionCompleted,
  adminCase,
  onSubmit,
  setValues,
  setIsDeleting,
  isEdited,
  setEdited,
  canUpdateCase,
}: CaseEditFormProps) => {
  const { unsupportedBillingType, setUnsupportedBillingType } = useContext(
    UnsupportedBillingTypeContext
  );
  const externalLabs = useAppSelector((state) =>
    selectExternalLabsForLab(
      state,
      adminCase.accessioning_lab?.id
        ? { ...adminCase.accessioning_lab, id: adminCase.accessioning_lab.id }
        : undefined
    )
  );
  const features = useAppSelector(({ sys }) => sys.features);

  // Optionally, sync local and context state if needed
  const [isOpenPopUp, setIsOpenPopUp] = useState<boolean>(false);
  const [warnings, setWarnings] = useState({});
  const canUpdateAccession =
    // PW cases shouldn't be directly accession-number editable
    adminCase?.source !== Case.source.PW;
  const canValidateForm = canValidate(
    adminCase?.patient?.clinic?.customer_type
  );

  const validationWarnings = (values: Object) => {
    canValidateForm &&
      caseWarningSchema
        .validate(values, {
          abortEarly: false,
        })
        .then(() => {
          setWarnings({});
        })
        .catch((err) => {
          setWarnings(getValidationWarnings(err));
        });
  };

  return (
    <>
      {isOpenPopUp && (
        <CaseEditPopUp
          adminCase={adminCase}
          onClose={() => setIsOpenPopUp(false)}
        />
      )}
      <Formik
        initialValues={initialValuesForCase(adminCase)}
        onSubmit={onSubmit}
        key={`${adminCase.accession}${adminCase.active_assignment?.id}`}
        validate={(values: any) => {
          validationWarnings(values);
        }}
        validationSchema={canValidateForm ? caseSchema : null}
        validateOnMount={true}
        enableReinitialize={unsupportedBillingType}
      >
        {({ errors, handleChange, resetForm, touched, values }) => {
          const allErrors = getErrors(errors, touched, !accessionCompleted);
          const allErrorsWithBillingType = {
            ...getErrors(errors, touched, !accessionCompleted),
            billing_type: "Change case billing type.",
          };
          const allWarnings = getErrors(warnings, touched);
          const isBillingComponentsDisabled =
            !canUpdateCase || isNoBillingConstrainedCaseType(values.type);

          return (
            <Form className="panelContainer">
              <FormikContext setValues={setValues} />
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Case Details
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="accession">
                    {({ field }: any) => (
                      <TextInput
                        label="Accession"
                        id="accession"
                        {...field}
                        onChange={(e: any) => {
                          handleChange(e);
                          setEdited(true);
                        }}
                        disabled={!canUpdateAccession || !canUpdateCase}
                      />
                    )}
                  </Field>
                  {adminCase.source === CaseAdmin.source.PW && (
                    <Field name="external_accession">
                      {({ field }: any) => (
                        <TextInput
                          label="External Accession"
                          id="external_accession"
                          {...field}
                          onChange={(e: any) => {
                            handleChange(e);
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                        />
                      )}
                    </Field>
                  )}
                  {adminCase.source === Case.source.PW &&
                    features?.externalGrossingLab &&
                    !!externalLabs?.length && (
                      <Field name="grossed_by_external_lab_id">
                        {({ field }: any) => (
                          <div className="field--minSize">
                            <SelectInput
                              label="Ext. Grossing Lab"
                              // TODO: imo `id` (and thus htmlFor for the label)
                              // ... should be implied through `name`
                              // (perhaps prefixed or otherwise namespaced or scaoped)
                              // Without duplicating this "name", label has no for
                              id="grossed_by_external_lab_id"
                              options={externalLabs.map((externalLab) => ({
                                label: externalLab.name,
                                value: externalLab.id,
                              }))}
                              {...field}
                              onChange={(e: any) => {
                                handleChange(e);
                                setEdited(true);
                              }}
                              disabled={!canUpdateCase}
                            />
                          </div>
                        )}
                      </Field>
                    )}
                  {adminCase.source === Case.source.PW &&
                    features?.caseType && (
                      <Field name="type">
                        {({ field, form }: any) => (
                          <div className="field--minSize field--halfSize">
                            <SelectInput
                              label="Type"
                              // TODO: imo `id` (and thus htmlFor for the label)
                              // ... should be implied through `name`
                              // (perhaps prefixed or otherwise namespaced or scaoped)
                              // Without duplicating this "name", label has no for
                              id="type"
                              testId="case-type-select"
                              options={[
                                { label: "", value: CaseAdmin.type.CLINICAL },
                                {
                                  label: "Consult",
                                  value: CaseAdmin.type.CONSULT,
                                },
                                {
                                  label: "TC-PC Split",
                                  value: CaseAdmin.type.TC_PC,
                                },
                                {
                                  label: "Historical",
                                  value: CaseAdmin.type.HISTORICAL,
                                },
                                {
                                  label: "Mohs QC",
                                  value: CaseAdmin.type.MOHS_QC,
                                },
                                {
                                  label: "Triage",
                                  value: CaseAdmin.type.TRIAGE,
                                },
                                { label: "Test", value: CaseAdmin.type.TEST },
                                {
                                  label: "Education",
                                  value: CaseAdmin.type.EDUCATION,
                                },
                                ...(!enabledCasetypes.includes(field.value)
                                  ? [
                                      {
                                        label: capitalize(field.value),
                                        value: field.value,
                                        disabled: true,
                                      },
                                    ]
                                  : []),
                              ]}
                              {...field}
                              onChange={(e: any) => {
                                const nextType = e.target.value;
                                const wasConstrained =
                                  isNoBillingConstrainedCaseType(values.type);
                                const nextIsConstrained =
                                  isNoBillingConstrainedCaseType(nextType);

                                if (nextIsConstrained) {
                                  form.setValues({
                                    ...form.values,
                                    ...getBillingProperties(null),
                                    billing_type: CaseAdmin.billing_type.NONE,
                                    billing_components: "none",
                                  });
                                } else if (wasConstrained) {
                                  // Switching from a constrained (no-billing)
                                  // type to an unconstrained one — clear
                                  // billing_type so the user must choose one
                                  // valid for the clinic.
                                  form.setValues({
                                    ...form.values,
                                    ...getBillingProperties(null),
                                    billing_type: "",
                                  });
                                }
                                handleChange(e);
                                setEdited(true);
                              }}
                              disabled={!canUpdateCase}
                              skipBlankOption
                            />
                          </div>
                        )}
                      </Field>
                    )}
                  <Field name="priority">
                    {({ field }: any) => (
                      <div className="field--minSize field--halfSize">
                        <SelectInput
                          label="Priority"
                          options={Object.values(CaseAdmin.priority).map(
                            (p) => ({
                              label: displayPriority(p),
                              value: p,
                            })
                          )}
                          {...field}
                          onChange={(e: any) => {
                            handleChange(e);
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="accessioning_lab">
                    {({ field, form }: any) => (
                      <div className="field__input--minSize">
                        <AdminFieldLab
                          {...field}
                          label="Accessioning Lab"
                          value={field.value}
                          onChange={(newValue: any) => {
                            form.setFieldValue(
                              "accessioning_lab",
                              newValue.object
                            );
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="grossed_by_cert">
                    {({ field, form }: any) => (
                      <div className="field__input--minSize">
                        {adminCase.grossed_by_cert ? (
                          <div>
                            <label>TC CLIA</label>
                            <CliaCertificateOption
                              object={
                                adminCase.grossed_by_cert as CliaCertificate
                              }
                            />
                          </div>
                        ) : (
                          <AdminFieldGrossedByCert
                            {...field}
                            labName={adminCase.accessioning_lab?.name}
                            value={field.value}
                            onChange={(newValue: any) => {
                              form.setFieldValue(
                                "grossed_by_cert",
                                newValue.object
                              );
                              setEdited(true);
                            }}
                            disabled={!canUpdateCase}
                          />
                        )}
                      </div>
                    )}
                  </Field>
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="collected_date">
                    {({ field, form }: any) => (
                      <CustomInput
                        label="Collected Date"
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "collected_date")}
                      >
                        <input
                          type="datetime-local"
                          label="Collected Date"
                          id="collected_date"
                          {...field}
                          onChange={(e: any) => {
                            form.setFieldValue(
                              "collected_date",
                              e.target.value
                            );
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                          max="9999-12-31T23:59"
                        />
                      </CustomInput>
                    )}
                  </Field>
                  <CustomInput label="Collected Date Timezone">
                    {getZoneString(adminCase?.collected_date_timezone)}
                  </CustomInput>
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="received_date">
                    {({ field, form }: any) => (
                      <CustomInput
                        label="Received Date"
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "received_date")}
                      >
                        <input
                          type="datetime-local"
                          label="Received Date"
                          id="received_date"
                          {...field}
                          onChange={(e: any) => {
                            form.setFieldValue("received_date", e.target.value);
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                          max="9999-12-31T23:59"
                        />
                      </CustomInput>
                    )}
                  </Field>
                  <CustomInput label="Received Date Timezone">
                    {getZoneString(adminCase?.received_date_timezone)}
                  </CustomInput>
                </div>
                <div className="panelContainer__inputGroup">
                  {adminCase.accessioning_lab?.settings?.bag_tracking
                    ?.active && (
                    <Field name="specimen_bag_barcode">
                      {({ field }: any) => (
                        <div className="panelContainer__status">
                          <TextInput
                            label="Specimen Bag Barcode"
                            id="specimen_bag_barcode"
                            {...field}
                            value={field.value || "None"}
                            disabled
                          />
                        </div>
                      )}
                    </Field>
                  )}
                  {adminCase.status === Case.status.FINALIZED &&
                    adminCase.finalized_at && (
                      <CustomInput label="Finalized At">
                        <span>
                          {DateTime.fromISO(adminCase.finalized_at).toFormat(
                            "MM/dd/yyyy, hh:mm a"
                          )}
                        </span>
                      </CustomInput>
                    )}
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Workflow
                </legend>
                <div className="panelContainer__inputGroup">
                  {adminCase.source === CaseAdmin.source.PW && (
                    <Field name="finalizing_signature">
                      {({ field, form }: any) => (
                        <AdminFieldSignature
                          {...field}
                          field="finalizing_signature"
                          label="Finalizing Signature"
                          serviceOptions={{ caseId: adminCase.id }}
                          onChange={(newValue: any) => {
                            form.setFieldValue(
                              "finalizing_signature",
                              newValue.object
                            );

                            if (form.values.is_screening) {
                              form.setFieldValue(
                                "finalize_assignment",
                                newValue.object.user
                              );
                            } else {
                              form.setFieldValue(
                                "active_assignment",
                                newValue.object.user
                              );
                            }
                            setEdited(true);
                          }}
                          useIsMissingInfo
                          disabled={!canUpdateCase}
                          warning={allWarnings.finalizing_signature}
                        />
                      )}
                    </Field>
                  )}
                  {adminCase.source === CaseAdmin.source.PW ? (
                    <Field name="active_assignment">
                      {({ field }: any) => {
                        if (!field.value) return null;
                        const { id, first_name, last_name } = field.value;
                        return (
                          <CustomInput label="Active Assignment">
                            <Option
                              index={id || ""}
                              info={{
                                main: `${first_name} ${last_name}`,
                              }}
                            />
                          </CustomInput>
                        );
                      }}
                    </Field>
                  ) : (
                    <Field name="active_assignment">
                      {({ field, form }: any) => (
                        <AdminFieldAssignment
                          {...field}
                          field="active_assignment"
                          label="Active Assignment"
                          onChange={(newValue: any) => {
                            form.setFieldValue(
                              "active_assignment",
                              newValue.object
                            );
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                          useIsMissingInfo
                        />
                      )}
                    </Field>
                  )}
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="screen_assignment">
                    {({ field, form }: any) => (
                      <AdminFieldAssignment
                        {...field}
                        field="screen_assignment"
                        label="Screen Assignment"
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            "screen_assignment",
                            newValue.object
                          );
                          if (form.values.is_screening) {
                            form.setFieldValue(
                              "active_assignment",
                              newValue.object
                            );
                          }
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                  <Field name="finalize_assignment">
                    {({ field }: any) => (
                      <CustomInput label="Finalize Assignment">
                        {field.value?.id ? (
                          <UserOption object={field.value} />
                        ) : (
                          <span>No Finalize Assignment</span>
                        )}
                      </CustomInput>
                    )}
                  </Field>
                  {adminCase.source === CaseAdmin.source.PW
                    ? adminCase.is_screening
                      ? features?.canCancelScreen && (
                          <PathologyWatchButton
                            className="field__screenButton"
                            color="red"
                            onClick={() => setIsOpenPopUp(true)}
                          >
                            Undo Screen
                          </PathologyWatchButton>
                        )
                      : features?.adminScreenSetup && (
                          <PathologyWatchButton
                            className="field__screenButton"
                            onClick={() => setIsOpenPopUp(true)}
                          >
                            Set Case To Screen
                          </PathologyWatchButton>
                        )
                    : null}
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="status">
                    {({ field, form }: any) => (
                      <div
                        className={classNames("field--minSize", {
                          panelContainer__status: !adminCase.glass_read,
                        })}
                      >
                        <SelectInput
                          label="Status"
                          disableDefault
                          options={[
                            ...(!enabledStatuses.includes(field.value)
                              ? [
                                  {
                                    label: field.value || "...",
                                    value: field.value || "",
                                    disabled: true,
                                  },
                                ]
                              : []),
                            ...enabledStatuses.map((p) => ({
                              label: p,
                              value: p,
                            })),
                          ]}
                          {...field}
                          onChange={(e: any) => {
                            form.setValues({
                              ...form.values,
                              status: e.target.value,
                            });

                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                        />
                      </div>
                    )}
                  </Field>
                  {adminCase.glass_read && (
                    <div className="panelContainer__glassRead">
                      <FontAwesomeIcon
                        icon={faMicroscope}
                        color="#666"
                        size="2x"
                      />
                      <div>
                        <strong className="panelContainer__glassLabel">
                          Glass Read
                        </strong>
                        <p>This case is intended to be read on glass.</p>
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
              {adminCase.source === CaseAdmin.source.PW && (
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    Billing Information
                  </legend>
                  <div className="panelContainer__inputGroup">
                    <div className="panelContainer__inputGroup">
                      <Field name="billing_type">
                        {({ field }: any) => {
                          // Get allowed billing types from clinic
                          const allowedBillingTypes =
                            adminCase?.patient?.clinic?.billing_types || [];

                          // Map to label/value pairs
                          const allBillingTypeOptions = [
                            {
                              label: "Third-Party Bill",
                              value: CaseAdmin.billing_type.GLOBAL,
                            },
                            {
                              label: "Client Bill",
                              value: CaseAdmin.billing_type.CLIENT,
                            },
                            {
                              label: "No Bill",
                              value: CaseAdmin.billing_type.NONE,
                            },
                          ];
                          // Filter by allowed types
                          const filteredOptions =
                            !features?.limitBillingTypesAdmin ||
                            (Array.isArray(allowedBillingTypes) &&
                              allowedBillingTypes.length === 0)
                              ? allBillingTypeOptions
                              : allBillingTypeOptions.filter((opt) =>
                                  allowedBillingTypes.includes(opt.value)
                                );

                          // If the current billing type is not in the allowed list, add it to the options. This happens for any existing cases that have a billing type that is no longer allowed for the clinic.
                          if (
                            currentBillingTypes.includes(field.value) &&
                            !allowedBillingTypes.includes(field.value) &&
                            features?.limitBillingTypesAdmin &&
                            allowedBillingTypes.length > 0
                          ) {
                            const unsupportedBillingTypeOption = Object.assign(
                              {},
                              allBillingTypeOptions.find(
                                (opt) => opt.value === field.value
                              ),
                              { disabled: true }
                            );

                            if (unsupportedBillingTypeOption) {
                              filteredOptions.push(
                                unsupportedBillingTypeOption
                              );
                            }
                          }

                          return (
                            <SelectInput
                              label="Billing Type"
                              id="billing_type"
                              options={filteredOptions}
                              {...field}
                              onChange={(e: any) => {
                                features?.limitBillingTypesAdmin &&
                                  setUnsupportedBillingType(
                                    isBillingTypeUnsupported(
                                      e.target.value,
                                      adminCase?.patient?.clinic?.billing_types
                                    )
                                  );

                                handleChange(e);
                                setEdited(true);
                              }}
                              disabled={isBillingComponentsDisabled}
                              error={
                                unsupportedBillingType &&
                                features?.limitBillingTypesAdmin
                                  ? "Error"
                                  : undefined
                              }
                              warning={allWarnings.billing_type}
                            />
                          );
                        }}
                      </Field>
                    </div>
                    <div className="panelContainer__inputGroup">
                      <Field name="billing_properties">
                        {({ field, form }: any) => (
                          <SelectInput
                            {...field}
                            value={getSelfPayProperties(form.values)}
                            label="Self Pay / Indigent"
                            id="billing_properties"
                            options={[
                              {
                                label: "Not Applicable",
                                value: "not_applicable",
                              },
                              {
                                label: "Self Pay",
                                value: "self_pay",
                              },
                              {
                                label: "Self Pay With Attestation",
                                value: "self_pay_with_attestation",
                              },
                              {
                                label: "Indigent",
                                value: "indigent",
                              },
                            ].map((p) => ({
                              label: p.label,
                              value: p.value,
                            }))}
                            onChange={(e: any) => {
                              form.setValues({
                                ...form.values,
                                ...getBillingProperties(e.target.value),
                              });

                              setEdited(true);
                            }}
                            skipBlankOption
                            disabled={isBillingComponentsDisabled}
                          />
                        )}
                      </Field>
                    </div>
                    <Field name="billing_components">
                      {({ field, form }: any) => (
                        <CustomInput label="Billing Component">
                          <div
                            className="panelContainer__inputGroup"
                            style={{ flex: "1" }}
                          >
                            <span className="panelContainer__checkbox">
                              <input
                                type="radio"
                                name="billing_component"
                                id="tc_only"
                                value="tc_only"
                                checked={field.value === "tc_only"}
                                onClick={() => {
                                  form.setFieldValue(
                                    "billing_components",
                                    field.value === "tc_only"
                                      ? "none"
                                      : "tc_only"
                                  );
                                  setEdited(true);
                                }}
                                onChange={() => {}}
                                disabled={isBillingComponentsDisabled}
                              />
                              <label htmlFor="tc_only">TC Only</label>
                            </span>
                            <span className="panelContainer__checkbox">
                              <input
                                type="radio"
                                name="billing_component"
                                id="pc_only"
                                value="pc_only"
                                checked={field.value === "pc_only"}
                                onClick={() => {
                                  form.setFieldValue(
                                    "billing_components",
                                    field.value === "pc_only"
                                      ? "none"
                                      : "pc_only"
                                  );
                                  setEdited(true);
                                }}
                                onChange={() => {}}
                                disabled={isBillingComponentsDisabled}
                              />
                              <label htmlFor="pc_only">PC Only</label>
                            </span>
                          </div>
                        </CustomInput>
                      )}
                    </Field>
                    <div className="panelContainer__inputGroup panelContainer__inputGroup--readonly">
                      <label>Biller</label>
                      <div>
                        <p>
                          <strong>{adminCase.biller?.name}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}
              <FormErrors
                errors={
                  unsupportedBillingType && features?.limitBillingTypesAdmin
                    ? allErrorsWithBillingType
                    : allErrors
                }
                warnings={allWarnings}
              />
              <div className="form__actionContainer">
                <PathologyWatchButton
                  title="There are no slides in this case"
                  color="red"
                  onClick={() => setIsDeleting(true)}
                  disabled={!!adminCase.slides.length || !canUpdateCase}
                >
                  Delete Case
                </PathologyWatchButton>
                <PathologyWatchButton
                  title="Discard changes to case"
                  color="red"
                  onClick={() => {
                    const fresh = initialValuesForCase(adminCase);

                    resetForm({ values: fresh });
                    validationWarnings(fresh);
                    setEdited(false);
                  }}
                  disabled={!isEdited}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  title="Save case"
                  disabled={
                    !isEdited ||
                    !canUpdateCase ||
                    Object.keys(allErrors).length > 0
                  }
                  color="green"
                >
                  Save Changes
                </PathologyWatchButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CaseEditForm;
